import { groq } from "next-sanity"

import { getEmployeeReference } from "@/graphql/schemas/shared/utils/getEmployeeReference"
import { AppSettings } from "@/settings"

import { internalCustomerException } from "../../customer/utils/customerQueryHelpers"
import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { noteProjection } from "../sanity/note.projection"

export const mutationUpdateNote = {
  async updateNote(parent, args, context, info) {
    const { lang = AppSettings.defaultLocale(), cdn = true, id, values } = args
    const { user } = context

    if (!id) return null

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const modifiedBy = await getEmployeeReference({
      employeeNumber: user.employeeNumber,
      sanityClient: client,
    })

    const updateValues = {
      customerNumber: values.customerNumber,
      lineNumber: values.lineNumber,
      text: values.text,
      displayDuringReceiptCreation: values.displayDuringReceiptCreation,
      modifiedAt: new Date().toISOString(),
      modificationSource: "Sanity",
      ...(modifiedBy && { modifiedBy }),
    }

    // remove all undefined values
    Object.keys(updateValues).forEach((key) => updateValues[key] === undefined && delete updateValues[key])

    const draftsFilter = `&& !(_id in path("drafts.**"))`

    try {
      // even though above patch will catch the error if id was not found,
      // we also need to check if the provided id is actually from note
      const note = await client.fetch(
        groq`*[_type=="note" && _id==$id ${draftsFilter}][0] {
            ${noteProjection()}
          }`,
        { id: id },
      )

      if (!note) return null

      await Promise.all([
        checkPermissions({
          user,
          domainObject: DomainObjects.NOTES,
          operation: Operations.UPDATE,
        }),
        validateAccessRightsToCustomer({ user, customerNumber: note.customerNumber }),
        internalCustomerException({ customerNumber: note.customerNumber }),
      ])

      // if id was not found, error will be cought
      await client.patch(id).set(updateValues).commit()

      const newNote = await client.fetch(
        groq`*[_type=="note" && _id==$id ${draftsFilter}][0] {
          ${noteProjection()}
        }`,
        { id: id },
      )

      return newNote ?? null
    } catch (error) {
      console.warn("Error updating note", error)
      return null
    }
  },
}
